(ns ui.openrouter.actions
  "Action handlers for the OpenRouter AI sidebar: category
   expand/collapse and the polling loop for asynchronous video jobs.

   The poll loop mirrors `:story/download-poll`: a 2.5s interval that
   stops after 5 minutes, when the user leaves the editor2 page, or when
   the job finishes. It keeps running when the modal is closed, since
   the generation is already paid for — the finished video is then still
   added to the story."
  (:require [app.openrouter.pricing :as pricing]
            [clojure.string :as str]
            [datascript.core :as ds]
            [parts.replicant.command :as command]
            [parts.replicant.query :as query]
            [ui.openrouter.catalog :as catalog]))

(def poll-interval-ms
  2500)

(def poll-timeout-ms
  (* 1000 60 5))

(defn poll-command
  [job-uuid]
  {:command/kind :command/openrouter-video-poll
   :command/data {:job-uuid job-uuid}})

(defn stop-poll!
  [store]
  (when-let [interval (get-in @store [:editor2 :ai-sidebar :video-poll])]
    (js/clearInterval interval))
  (swap! store update-in [:editor2 :ai-sidebar] dissoc :video-poll))

(defn toggle-category
  [{:keys [store action]}]
  (let [category-id (second action)]
    (swap! store
           update-in
           [:editor2 :ai-sidebar :expanded]
           (fn [expanded]
             (let [expanded (or expanded #{})]
               (if (contains? expanded category-id)
                 (disj expanded category-id)
                 (conj expanded category-id)))))))

(defn find-catalog-model
  [state category-id model-id]
  (some->> (:openrouter/categories (query/get-result state
                                                     catalog/models-query))
           (filter #(= category-id (:category/id %)))
           (first)
           (:category/models)
           (filter #(= model-id (:model/id %)))
           (first)))

(defn pick-supported
  "Keeps `value` when the newly selected model supports it, otherwise
   falls back to `default`. nil when the model has no such options."
  [value options default]
  (cond
    (empty? options) nil
    (some #(= (str %) (str value)) options) value
    :else default))

(defn switch-model
  "Switches the modal to another model of the same category. The form
   data (prompt, source image, ...) is kept; options the new model does
   not support fall back to its defaults."
  [{:keys [store action]}]
  (let [model-id (second action)
        state @store
        category-id (get-in state [:ui/modal :params :category-id])
        model (find-catalog-model state category-id model-id)]
    (when model
      (swap! store
             (fn [state]
               (-> state
                   (assoc-in [:ui/modal :params :model] model)
                   (update-in [:ui/modal :openrouter/data]
                              (fn [data]
                                (assoc data
                                       :aspect-ratio (pick-supported
                                                       (:aspect-ratio data)
                                                       (:model/aspect-ratios model)
                                                       (pricing/default-aspect-ratio (:model/aspect-ratios model)))
                                       :voice (pick-supported
                                                (:voice data)
                                                (:model/voices model)
                                                (first (:model/voices model)))
                                       :resolution (pick-supported
                                                     (:resolution data)
                                                     (:model/resolutions model)
                                                     (pricing/default-resolution (:model/resolutions model)))
                                       :duration (pick-supported
                                                   (:duration data)
                                                   (:model/durations model)
                                                   (pricing/default-duration (:model/durations model))))))
                   ;; The error/spinner state of a previous attempt
                   ;; belongs to the previous model:
                   (update :ui/modal dissoc :openrouter/submitted-command)))))))

(defn select-source-image
  "Selects the source image for image editing. When the picked image was
   itself AI generated, its prompt prefills the textarea — unless the
   user already typed a prompt themselves (their words are never
   overwritten)."
  [{:keys [store action]}]
  (let [{:keys [asset-uuid source-image-url prompt]} (second action)]
    (swap! store
           update-in
           [:ui/modal :openrouter/data]
           assoc
           :source-asset-uuid asset-uuid
           :source-image-url source-image-url)
    (when (and (seq prompt)
               (or (str/blank? (get-in @store [:ui/modal :openrouter/data :prompt]))
                   (not= :user (get-in @store [:ui/modal :openrouter/prompt-source]))))
      (swap! store assoc-in [:ui/modal :openrouter/data :prompt] prompt)
      (swap! store assoc-in [:ui/modal :openrouter/prompt-source] :prefill))))

(defn show-asset-tooltip
  "Captures the hovered AI badge's viewport position, so the tooltip can
   be rendered with a fixed position above the surrounding UI (the
   sidebar's scroll container would clip an absolutely positioned one)."
  [{:keys [store action] :as w}]
  (let [asset-uuid (second action)
        rect (.getBoundingClientRect
               (.-currentTarget (:replicant/js-event w)))]
    (swap! store
           assoc-in
           [:editor2 :ai-sidebar :tooltip]
           {:asset-uuid asset-uuid
            :x (.-right rect)
            :y (.-bottom rect)})))

(defn hide-asset-tooltip
  [{:keys [store]}]
  (swap! store update-in [:editor2 :ai-sidebar] dissoc :tooltip))

(defn regenerate
  "Opens the generation modal for an existing AI asset (via the timeline
   double click or the AI badge in the media sidebar), prefilled with
   the asset's stored recipe. The new generation replaces the asset in
   the timeline."
  [{:keys [store action ui/event-handler]}]
  (let [{:keys [asset-uuid clip-uuid]} (second action)
        state @store
        db (get-in state [:editor2 :db])
        asset (cond
                asset-uuid (ds/entity db [:asset/uuid asset-uuid])
                clip-uuid (:clip/asset (ds/entity db [:clip/uuid clip-uuid])))
        data (:asset/data asset)
        category-id (:ai/category data)]
    (when category-id
      (let [model (or (find-catalog-model state
                                          category-id
                                          (:ai/model-id data))
                      ;; The catalog may not be loaded yet — the modal
                      ;; upgrades this stub once the query result lands:
                      {:model/id (:ai/model-id data)
                       :model/name (:ai/model-id data)})]
        (event-handler {}
                       [[:ui/modal-open :modals/openrouter-generate
                         {:model model
                          :category-id category-id
                          :source :editor2
                          :regenerate-asset-uuid (:asset/uuid asset)}]])))))

(defn video-poll-start
  "Dispatched via the video start command's :on-success. Reads the
   job-uuid from the command result and starts the poll interval."
  [{:keys [store action ui/event-handler]}]
  (let [params (second action)
        result (command/get-latest-result @store (:command params))
        job-uuid (:job-uuid result)
        started-at (js/Date.now)]
    (when job-uuid
      (stop-poll! store)
      (swap! store assoc-in [:ui/modal :openrouter/video-job-uuid] job-uuid)
      (swap! store
             assoc-in
             [:editor2 :ai-sidebar :video-insert-params]
             (select-keys params [:ai-data :replace-asset-uuid]))
      (swap! store
             assoc-in
             [:editor2 :ai-sidebar :video-poll]
             (js/setInterval
               (fn []
                 (let [page-id (get-in @store [:location :location/page-id])]
                   (cond
                     (not= :pages/editor2 page-id)
                     (stop-poll! store)

                     (> (- (js/Date.now) started-at) poll-timeout-ms)
                     (do
                       (stop-poll! store)
                       (swap! store
                              assoc-in
                              [:ui/modal :openrouter/video-error]
                              "The video generation timed out. Please contact support."))

                     ;; No new poll while one is still in flight: the
                     ;; completing poll downloads the video server-side
                     ;; and can take longer than the interval, which
                     ;; would otherwise pile up further polls that all
                     ;; return :completed.
                     (command/issued? @store (poll-command job-uuid))
                     nil

                     :else
                     (event-handler {}
                                    [[:data/command (poll-command job-uuid)
                                      {:on-success [[:openrouter/video-poll-handle
                                                     {:job-uuid job-uuid}]]
                                       :on-error [[:openrouter/video-poll-failed
                                                   {:job-uuid job-uuid}]]}]]))))
               poll-interval-ms)))))

(defn video-poll-handle
  [{:keys [store action]}]
  (let [params (second action)
        job-uuid (:job-uuid params)
        result (command/get-latest-result @store (poll-command job-uuid))
        handled (get-in @store [:editor2 :ai-sidebar :video-handled] #{})
        dispatch! (:replicant/dispatch! @store)]
    (when (and (= :completed (:status result))
               ;; A completed job inserts its clip exactly once, even
               ;; when several poll responses report :completed:
               (not (contains? handled job-uuid)))
      (swap! store
             update-in
             [:editor2 :ai-sidebar :video-handled]
             (fnil conj #{})
             job-uuid)
      (stop-poll! store)
      (dispatch! {:actions [[:editor2/add-ai-video
                             (merge {:video-url (:video-generation/video-url result)}
                                    (get-in @store
                                            [:editor2 :ai-sidebar :video-insert-params]))]
                            [:store/assoc-in [:editor2 :sidebar-panel] :media]
                            [:ui/modal-close]]}))))

(defn video-poll-failed
  [{:keys [store action]}]
  (let [params (second action)
        job-uuid (:job-uuid params)
        result (command/get-latest-result @store (poll-command job-uuid))
        error (or (:error result)
                  "Video generation failed")]
    (stop-poll! store)
    (swap! store assoc-in [:ui/modal :openrouter/video-error] error)))

(def register
  [{:ui.action/kind :openrouter/toggle-category
    :ui.action/handler toggle-category}
   {:ui.action/kind :openrouter/select-source-image
    :ui.action/handler select-source-image}
   {:ui.action/kind :openrouter/switch-model
    :ui.action/handler switch-model}
   {:ui.action/kind :openrouter/regenerate
    :ui.action/handler regenerate}
   {:ui.action/kind :openrouter/show-asset-tooltip
    :ui.action/handler show-asset-tooltip}
   {:ui.action/kind :openrouter/hide-asset-tooltip
    :ui.action/handler hide-asset-tooltip}
   {:ui.action/kind :openrouter/video-poll-start
    :ui.action/handler video-poll-start}
   {:ui.action/kind :openrouter/video-poll-handle
    :ui.action/handler video-poll-handle}
   {:ui.action/kind :openrouter/video-poll-failed
    :ui.action/handler video-poll-failed}])
